/**
 * Exposure Alerts configuration card with view mode + edit modal pattern.
 *
 * Features:
 * - View mode showing summary of enabled alerts (common exposures + categories)
 * - Edit button opens modal with CategoryExposureAlertsForm
 * - Consistent with SettingEditableCard pattern
 *
 * Used in:
 * - NotificationSettingsCards
 */

import { useMemo } from 'react';

import { Loader } from '@/components/Loader';
import { useMy } from '@/hooks/useMy';
import {
  deriveSelectedCategories,
  EXPOSURE_CATEGORIES,
  getCustomPorts,
  getCustomProtocols,
} from '@/sections/settings/components/cards/exposureAlertCategories';
import { CategoryExposureAlertsForm } from '@/sections/settings/components/forms/CategoryExposureAlertsForm';
import { SettingEditableCard } from '@/sections/settings/components/SettingEditableCard';
import { useAuth } from '@/state/auth';
import { Condition, Setting } from '@/types';

/**
 * Exposure settings type - maps risk names to notification preferences
 */
type ExposureSettings = Record<string, boolean>;

/**
 * Display names for common exposure types
 */
const COMMON_EXPOSURE_NAMES: Record<string, string> = {
  'web-login': 'Web Logins',
  'llm-chatbot': 'LLM Chatbots',
};

interface ExposureAlertsCardProps {
  /**
   * Email for impersonation - if provided, will fetch data for this user
   * If not provided, uses the current authenticated user
   */
  customerEmail?: string;
}

/**
 * Exposure Alerts card with view mode showing current configuration.
 * Edit button opens modal with full CategoryExposureAlertsForm.
 */
export function ExposureAlertsCard({ customerEmail }: ExposureAlertsCardProps) {
  const { me, friend } = useAuth();
  // Use same email logic as useUpdateSetting to ensure query key matches for invalidation
  const effectiveEmail = customerEmail || friend || me;

  // Fetch current conditions for Advanced tab display
  const { data: conditions, status: conditionsStatus } = useMy(
    { resource: 'condition' },
    { email: effectiveEmail }
  );

  // Fetch current settings for Standard tab display
  const { data: settings, status: settingsStatus } = useMy(
    { resource: 'setting' },
    { email: effectiveEmail }
  );

  // Process common exposures (Standard tab)
  const { enabledExposures, notifyExposures } = useMemo(() => {
    const exposureSettingRaw = settings?.find(
      (s: Setting) => s.name === 'exposure_settings'
    );
    const exposureSettings: ExposureSettings =
      (exposureSettingRaw?.value as ExposureSettings) || {};

    const enabled: string[] = [];
    const notify: string[] = [];

    Object.entries(exposureSettings).forEach(([key, shouldNotify]) => {
      const displayName = COMMON_EXPOSURE_NAMES[key] || key;
      enabled.push(displayName);
      if (shouldNotify) {
        notify.push(displayName);
      }
    });

    return {
      enabledExposures: enabled,
      notifyExposures: notify,
    };
  }, [settings]);

  // Process port/protocol categories (Advanced tab)
  const { selectedCategories, customPortsCount, customProtocolsCount } =
    useMemo(() => {
      const activePorts = new Set<number>();
      const activeProtocols = new Set<string>();

      (conditions || []).forEach((condition: Condition) => {
        if (condition.name === 'port') {
          activePorts.add(Number(condition.value));
        } else if (condition.name === 'protocol') {
          activeProtocols.add(condition.value);
        }
      });

      const categoryIds = deriveSelectedCategories(activePorts);
      const categoryNames = categoryIds
        .map(id => EXPOSURE_CATEGORIES.find(c => c.id === id)?.name)
        .filter(Boolean) as string[];

      const customPorts = getCustomPorts(activePorts);
      const customProtos = getCustomProtocols(activeProtocols);

      return {
        selectedCategories: categoryNames,
        customPortsCount: customPorts.length,
        customProtocolsCount: customProtos.length,
      };
    }, [conditions]);

  const isLoading =
    conditionsStatus === 'pending' || settingsStatus === 'pending';

  // Format list for display
  const formatList = (items: string[], maxShow: number = 3): string => {
    if (items.length === 0) return 'None';
    if (items.length <= maxShow) return items.join(', ');
    return `${items.slice(0, maxShow).join(', ')}, +${items.length - maxShow} more`;
  };

  const totalCustomItems = customPortsCount + customProtocolsCount;

  return (
    <section
      aria-labelledby="exposure-alerts-heading"
      data-testid="exposure-alerts-section"
    >
      <SettingEditableCard
        data-testid="exposure-alerts-card"
        editConfig={{
          modalTitle: 'Exposure Alert Settings',
          formComponent: CategoryExposureAlertsForm,
          formProps: { customerEmail },
          modalSize: '5xl',
        }}
      >
        <Loader
          isLoading={isLoading}
          className="mx-auto flex justify-center py-4"
        >
          <div className="flex flex-col gap-3">
            {/* Title */}
            <div className="flex flex-col gap-1">
              <h4
                id="exposure-alerts-heading"
                className="text-lg font-semibold"
              >
                Exposure Alerts
              </h4>
              <p className="text-chariot-text-secondary text-sm">
                Get notified when exposures are detected
              </p>
            </div>

            {/* Common Exposures Summary */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-chariot-text-secondary">
                Common Exposures:
              </span>
              <span className="text-chariot-text-primary">
                {enabledExposures.length > 0
                  ? `${formatList(enabledExposures)} (${notifyExposures.length} with notifications)`
                  : 'None'}
              </span>
            </div>

            {/* Port Categories Summary */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-chariot-text-secondary">
                Port Categories:
              </span>
              <span className="text-chariot-text-primary">
                {formatList(selectedCategories)}
              </span>
            </div>

            {/* Custom Items Summary */}
            {totalCustomItems > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-chariot-text-secondary">Custom:</span>
                <span className="text-chariot-text-primary">
                  {totalCustomItems} item{totalCustomItems !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </Loader>
      </SettingEditableCard>
    </section>
  );
}
